import type { BuildOrderStatus, DeliveryReport } from './buildApi';

export function groupAttention<T extends { order_id?: number; kind: string; error_code: string; item_key: string }>(rows: T[]): { key: string; row: T; items: string[] }[] {
  const groups = new Map<string, { key: string; row: T; items: string[] }>();
  for (const row of rows) {
    const key = `${row.order_id ?? ''}:${row.kind}:${row.error_code}`;
    const group = groups.get(key);
    if (group) group.items.push(row.item_key);
    else groups.set(key, { key, row, items: [row.item_key] });
  }
  return [...groups.values()];
}

export function isDelivered(status: BuildOrderStatus): boolean {
  return status === 'delivered' || status === 'closed';
}

/** Translate known historical limitations without turning an API failure into success. */
export function historicalReadNote(reason: string | undefined): string | null {
  if (!reason) return null;
  const unsupported = /^not verifiable for (PlusVibe|Bison) yet$/.exec(reason);
  if (unsupported) return `This saved report predates independent ${unsupported[1]} verification. Counts were recorded during the build.`;
  if (/API key purged (at close|after 72h idle)$/.test(reason)) {
    return 'The borrowed API key was removed after the build. These are saved build results; a fresh provider check is unavailable.';
  }
  return null;
}

export function hasHistoricalQaGap(report: DeliveryReport, status: BuildOrderStatus): boolean {
  if (!isDelivered(status) || report.verdict !== 'partial' || report.issues.length > 0
    || report.totals.abandoned_domains > 0 || report.totals.abandoned_mailboxes > 0) return false;
  const stacks = Object.values(report.stacks);
  const unread = stacks.filter((stack) => stack.read_error !== undefined);
  return unread.length > 0 && unread.every((stack) => historicalReadNote(stack.read_error) !== null)
    && stacks.every((stack) => stack.pass || stack.read_error !== undefined);
}
